import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Send, Loader2, Check, Target, Heart, Rocket, Mail, MessageSquare, Linkedin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { SiteFooter } from "@/components/SiteFooter";
import { API } from "@/utils/constants";

const PageShell = ({ title, subtitle, children }) => (
  <div className="min-h-screen bg-background text-foreground flex flex-col">
    <nav className="border-b border-border bg-card/80 backdrop-blur-lg sticky top-0 z-30">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
        <a href="/" className="flex items-center gap-2" data-testid="company-page-logo">
          <img src="/favicon.png" alt="Opero Smart" className="h-7 w-7 rounded-lg" />
          <span className="font-heading font-bold text-lg">Opero Smart</span>
        </a>
        <a href="/" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1.5 transition-colors" data-testid="company-page-back"><ArrowLeft className="h-4 w-4" />Back to home</a>
      </div>
    </nav>
    <div className="flex-1">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <h1 className="text-3xl sm:text-4xl font-bold mb-3">{title}</h1>
        {subtitle && <p className="text-muted-foreground mb-10">{subtitle}</p>}
        {children}
      </div>
    </div>
    <SiteFooter />
  </div>
);

export const AboutPage = () => (
  <PageShell title="About Opero Smart" subtitle="Project management that respects your time.">
    <div className="space-y-10" data-testid="about-page">
      <section>
        <p className="text-base leading-relaxed text-muted-foreground">
          Opero Smart was born from a simple frustration: project tools had become heavier than the projects themselves.
          Teams were paying for five different apps — a board here, a chat there, a time tracker somewhere else — and still
          spending hours keeping them in sync. We believed there was a better way.
        </p>
      </section>
      <div className="grid sm:grid-cols-3 gap-5">
        {[
          { icon: Target, title: "Our Mission", text: "Give every team — from solo founders to growing companies — one clean workspace where work actually moves forward." },
          { icon: Heart, title: "Our Values", text: "Simplicity over feature bloat. Fair pricing (20% below competitors). Privacy by default with per-user data isolation." },
          { icon: Rocket, title: "How We Ship", text: "We release new features every week, guided by a public roadmap and direct feedback from our users." },
        ].map((v, i) => (
          <div key={i} className="bg-card border rounded-2xl p-6">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center mb-3"><v.icon className="h-5 w-5 text-primary" /></div>
            <h3 className="font-semibold mb-2">{v.title}</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">{v.text}</p>
          </div>
        ))}
      </div>
      <section className="bg-muted/40 border rounded-2xl p-6 sm:p-8">
        <h2 className="text-lg font-semibold mb-3">Why we built it</h2>
        <p className="text-sm sm:text-base text-muted-foreground leading-relaxed mb-4">
          Modern teams don't need more software — they need less friction. Opero Smart combines Kanban boards, an AI task
          assistant, real-time team chat, Gantt timelines, workload views, automations and analytics into a single product
          that loads fast, works on your phone, and stays out of your way.
        </p>
        <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
          Today, teams around the world use Opero Smart to plan sprints, run client work, and ship products.
          We're just getting started — check our <a href="/#roadmap" className="text-primary hover:underline">roadmap</a> to see what's next.
        </p>
      </section>
      <div className="text-center">
        <Button size="lg" onClick={() => { window.location.href = "/"; }} data-testid="about-cta">Try Opero Smart Free</Button>
      </div>
    </div>
  </PageShell>
);

export const ContactPage = () => {
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("email", form.email);
      fd.append("company", form.company);
      fd.append("description", `[Contact form] ${form.message}`);
      await axios.post(`${API}/support-ticket`, fd);
      setSent(true);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to send. Please email us directly."); }
    setSending(false);
  };

  return (
    <PageShell title="Contact Us" subtitle="Questions, feedback or partnership ideas? We'd love to hear from you.">
      <div className="grid md:grid-cols-5 gap-8" data-testid="contact-page">
        <div className="md:col-span-3">
          {sent ? (
            <div className="bg-card border rounded-2xl p-8 text-center" data-testid="contact-success">
              <div className="w-14 h-14 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto mb-4"><Check className="h-7 w-7 text-emerald-500" /></div>
              <h3 className="text-lg font-semibold mb-2">Message sent!</h3>
              <p className="text-sm text-muted-foreground">We'll get back to you within 24 hours.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="bg-card border rounded-2xl p-6 sm:p-8 space-y-4" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <div><label className="text-sm font-medium mb-1.5 block">Name *</label><Input required value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Your name" data-testid="contact-name" /></div>
                <div><label className="text-sm font-medium mb-1.5 block">Email *</label><Input required type="email" value={form.email} onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))} placeholder="you@company.com" data-testid="contact-email" /></div>
              </div>
              <div><label className="text-sm font-medium mb-1.5 block">Company</label><Input value={form.company} onChange={(e) => setForm(f => ({ ...f, company: e.target.value }))} placeholder="Your company (optional)" data-testid="contact-company" /></div>
              <div><label className="text-sm font-medium mb-1.5 block">Message *</label><Textarea required rows={5} value={form.message} onChange={(e) => setForm(f => ({ ...f, message: e.target.value }))} placeholder="How can we help?" data-testid="contact-message" /></div>
              <Button type="submit" className="w-full" disabled={sending} data-testid="contact-submit">
                {sending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}{sending ? "Sending..." : "Send Message"}
              </Button>
            </form>
          )}
        </div>
        <div className="md:col-span-2 space-y-4">
          {[
            { icon: Mail, title: "Email", text: "support@operosmart.com", href: "mailto:support@operosmart.com" },
            { icon: MessageSquare, title: "Request a demo", text: "Get a personalized walkthrough", href: "/" },
            { icon: Linkedin, title: "LinkedIn", text: "Follow us for updates", href: "https://www.linkedin.com/company/operosmart/" },
          ].map((c, i) => (
            <a key={i} href={c.href} className="flex items-start gap-3 bg-card border rounded-xl p-4 hover:border-primary/40 transition-colors">
              <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0"><c.icon className="h-4.5 w-4.5 text-primary" /></div>
              <div><div className="text-sm font-semibold">{c.title}</div><div className="text-xs text-muted-foreground">{c.text}</div></div>
            </a>
          ))}
        </div>
      </div>
    </PageShell>
  );
};

const LegalSection = ({ title, children }) => (
  <section className="mb-8">
    <h2 className="text-lg font-semibold mb-2">{title}</h2>
    <div className="text-sm text-muted-foreground leading-relaxed space-y-2">{children}</div>
  </section>
);

export const PrivacyPage = () => (
  <PageShell title="Privacy Policy" subtitle={`Last updated: June 2026`}>
    <div data-testid="privacy-page">
      <LegalSection title="1. Information We Collect">
        <p>When you use Opero Smart, we collect: (a) account information such as your name, email address and profile picture provided during registration or Google sign-in; (b) content you create, including tasks, projects, comments, chat messages and uploaded files; (c) usage data such as feature interactions and log data used to improve the product.</p>
      </LegalSection>
      <LegalSection title="2. How We Use Your Information">
        <p>We use your information to provide and improve the service, sync your data across devices, send transactional emails (task assignments, mentions, overdue alerts), process payments through Stripe, and respond to support requests. We do not sell your personal data to third parties.</p>
      </LegalSection>
      <LegalSection title="3. Data Storage & Security">
        <p>Your data is stored with per-user isolation — your tasks and projects are private to you and the teammates you explicitly invite. Shared links can be password-protected with optional expiry. All traffic is encrypted in transit via HTTPS.</p>
      </LegalSection>
      <LegalSection title="4. Third-Party Services">
        <p>We use trusted processors: Stripe for payments (we never store card numbers), Google for optional OAuth sign-in, and Resend for transactional email delivery. Each processor handles data under its own privacy policy.</p>
      </LegalSection>
      <LegalSection title="5. Cookies & Sessions">
        <p>We use a session cookie to keep you signed in and local storage for preferences such as dark mode. We do not use third-party advertising trackers.</p>
      </LegalSection>
      <LegalSection title="6. Your Rights">
        <p>You may export your data (CSV/JSON) at any time, request correction of inaccurate data, or request deletion of your account and all associated data by contacting <a href="mailto:support@operosmart.com" className="text-primary hover:underline">support@operosmart.com</a>.</p>
      </LegalSection>
      <LegalSection title="7. Changes to This Policy">
        <p>We may update this policy from time to time. Material changes will be announced in-app or by email. Continued use of the service after changes constitutes acceptance.</p>
      </LegalSection>
      <LegalSection title="8. Contact">
        <p>Questions about privacy? Contact us at <a href="mailto:support@operosmart.com" className="text-primary hover:underline">support@operosmart.com</a>.</p>
      </LegalSection>
    </div>
  </PageShell>
);

export const TermsPage = () => (
  <PageShell title="Terms of Service" subtitle={`Last updated: June 2026`}>
    <div data-testid="terms-page">
      <LegalSection title="1. Acceptance of Terms">
        <p>By creating an account or using Opero Smart ("the Service"), you agree to these Terms of Service. If you use the Service on behalf of an organization, you represent that you have authority to bind that organization.</p>
      </LegalSection>
      <LegalSection title="2. The Service">
        <p>Opero Smart provides project management tools including task boards, team collaboration, chat, calendars, automations and analytics. We offer a Free plan and a paid Pro plan with additional features as described on our pricing page.</p>
      </LegalSection>
      <LegalSection title="3. Accounts & Responsibilities">
        <p>You are responsible for maintaining the confidentiality of your account credentials and for all activity under your account. You agree not to misuse the Service, including attempting unauthorized access, distributing malware, or using it for unlawful content.</p>
      </LegalSection>
      <LegalSection title="4. Subscriptions & Billing">
        <p>Pro subscriptions are billed monthly or yearly via Stripe. The Pro trial requires no credit card. You may cancel at any time; access continues until the end of the paid period. Fees are non-refundable except where required by law.</p>
      </LegalSection>
      <LegalSection title="5. Your Content">
        <p>You retain ownership of all content you create in Opero Smart. You grant us a limited license to store, process and display that content solely to operate the Service. We do not use your content to train AI models.</p>
      </LegalSection>
      <LegalSection title="6. Availability & Changes">
        <p>We strive for high availability but the Service is provided "as is" without warranties. We may modify or discontinue features with reasonable notice for material changes.</p>
      </LegalSection>
      <LegalSection title="7. Limitation of Liability">
        <p>To the maximum extent permitted by law, Opero Smart shall not be liable for indirect, incidental or consequential damages. Our total liability is limited to the amount you paid us in the twelve months preceding the claim.</p>
      </LegalSection>
      <LegalSection title="8. Termination">
        <p>You may delete your account at any time. We may suspend accounts that violate these terms. Upon termination, you may request an export of your data within 30 days.</p>
      </LegalSection>
      <LegalSection title="9. Contact">
        <p>Questions about these terms? Contact <a href="mailto:support@operosmart.com" className="text-primary hover:underline">support@operosmart.com</a>.</p>
      </LegalSection>
    </div>
  </PageShell>
);
